import { API_BASE } from '../config/api';
import AuthService from './authService';
import { getDisplayLocale } from '../utils/dateUtils';

class WeatherService {
  // Weather API calls are proxied through the backend to keep the API key server-side.
  // The backend reads WEATHER_API_KEY (no REACT_APP_ prefix).
  static ONE_CALL_URL = `${API_BASE}/api/weather/onecall`;
  static GEO_URL = `${API_BASE}/api/weather/geocode`;
  static OPEN_METEO_HISTORICAL_URL = 'https://archive-api.open-meteo.com/v1/archive';

  // Cache for location coordinates to avoid repeated geocoding calls
  static GEOCODE_CACHE = {};

  // Cache for weather data to minimize API calls (key: "lat,lon", value: { data, timestamp })
  static WEATHER_CACHE = {};
  static CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

  // Cache for historical climate data (key: "lat,lon,month", value: { data, timestamp })
  static CLIMATE_CACHE = {};
  static CLIMATE_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours (climate data doesn't change often)

  // Clean and normalize location strings for better geocoding results
  // Handles formats like "Dubai - United Arab Emirates" → "Dubai, AE"
  static cleanLocationString(location) {
    if (!location) return '';

    let cleaned = location.trim();

    // Common country name to code mappings for better geocoding
    const countryMappings = {
      'united arab emirates': 'AE',
      'united states': 'US',
      'united kingdom': 'GB',
      'united states of america': 'US',
      'uae': 'AE',
      'usa': 'US',
      'uk': 'GB'
    };

    // Replace " - " with ", " for consistent formatting
    cleaned = cleaned.replace(/\s*-\s*/g, ', ');

    // Check if location contains a full country name and replace with code
    const lowerCleaned = cleaned.toLowerCase();
    for (const [countryName, countryCode] of Object.entries(countryMappings)) {
      if (lowerCleaned.includes(countryName)) {
        // Extract the city part (before the country)
        const parts = cleaned.split(',').map(p => p.trim());
        if (parts.length >= 1) {
          const city = parts[0];
          cleaned = `${city}, ${countryCode}`;
          break;
        }
      }
    }

    // Remove extra commas and whitespace
    cleaned = cleaned.replace(/,\s*,/g, ',').replace(/\s+/g, ' ').trim();

    return cleaned;
  }

  // Geocode a location name to coordinates using OpenWeatherMap Geocoding API
  static async geocodeLocation(location) {
    // Clean and normalize the location string for better geocoding results
    const cleanedLocation = this.cleanLocationString(location);

    // Check cache first
    const cacheKey = cleanedLocation.toLowerCase().trim();
    if (this.GEOCODE_CACHE[cacheKey]) {
      console.log('Using cached coordinates for', location);
      return this.GEOCODE_CACHE[cacheKey];
    }

    try {
      const url = `${this.GEO_URL}?q=${encodeURIComponent(cleanedLocation)}&limit=1`;
      // Backend weather routes require auth (server.js mounts them behind
      // authenticateToken to protect the metered upstream API).
      const response = await fetch(url, { headers: AuthService.getAuthHeaders() });

      if (!response.ok) {
        throw new Error(`Geocoding failed: ${response.status}`);
      }

      const data = await response.json();

      if (data && data.length > 0) {
        const result = {
          latitude: data[0].lat,
          longitude: data[0].lon,
          name: data[0].name,
          country: data[0].country,
          state: data[0].state || null
        };
        // Cache the result
        this.GEOCODE_CACHE[cacheKey] = result;
        console.log('Geocoded', location, 'to', result.latitude, result.longitude);
        return result;
      }

      // No results found, use fallback
      console.log('No geocoding results for', location, ', using fallback');
      return this.getFallbackCoordinates(location);
    } catch (error) {
      console.error('Geocoding error:', error);
      return this.getFallbackCoordinates(location);
    }
  }

  // Fetch weather from One Call API 3.0
  static async fetchOneCallWeather(lat, lon) {
    const cacheKey = `${lat.toFixed(2)},${lon.toFixed(2)}`;
    const cached = this.WEATHER_CACHE[cacheKey];

    // Return cached data if still valid
    if (cached && (Date.now() - cached.timestamp) < this.CACHE_TTL_MS) {
      console.log('Using cached weather data for', cacheKey);
      return cached.data;
    }

    try {
      const url = `${this.ONE_CALL_URL}?lat=${lat}&lon=${lon}&units=metric`;
      // Backend weather routes require auth (see geocodeLocation above).
      // The Open-Meteo fetch below stays headerless — third-party host.
      const response = await fetch(url, { headers: AuthService.getAuthHeaders() });

      if (!response.ok) {
        throw new Error(`Weather API failed: ${response.status}`);
      }

      const data = await response.json();

      // Cache the result
      this.WEATHER_CACHE[cacheKey] = {
        data,
        timestamp: Date.now()
      };

      console.log('Fetched fresh weather data for', cacheKey);
      return data;
    } catch (error) {
      console.error('Weather API error:', error);
      return null;
    }
  }

  // Map One Call API daily forecast to our internal format
  static mapOneCallDayToInternal(day) {
    // Map weather condition code to our condition string
    const conditionMap = {
      'Clear': 'clear',
      'Clouds': 'cloudy',
      'Few clouds': 'partly_cloudy',
      'Scattered clouds': 'partly_cloudy',
      'Broken clouds': 'cloudy',
      'Overcast clouds': 'overcast',
      'Rain': 'rain',
      'Light rain': 'light_rain',
      'Moderate rain': 'rain',
      'Heavy rain': 'heavy_rain',
      'Drizzle': 'light_rain',
      'Thunderstorm': 'thunderstorm',
      'Snow': 'snow',
      'Mist': 'mist',
      'Fog': 'fog'
    };

    const weatherMain = day.weather?.[0]?.main || 'Clear';
    const weatherDesc = day.weather?.[0]?.description || '';
    const condition = conditionMap[weatherMain] || conditionMap[weatherDesc] || 'partly_cloudy';

    return {
      date: new Date(day.dt * 1000).toISOString().split('T')[0],
      conditions: condition,
      condition: condition,
      // Flat high/low for component compatibility
      high: Math.round(day.temp.max),
      low: Math.round(day.temp.min),
      // Nested temperature for backward compatibility
      temperature: {
        high: Math.round(day.temp.max),
        low: Math.round(day.temp.min)
      },
      precipitation: {
        probability: Math.round((day.pop || 0) * 100),
        expectedMm: Math.round((day.rain || 0) + (day.snow || 0))
      },
      humidity: day.humidity,
      windSpeed: Math.round(day.wind_speed * 3.6), // Convert m/s to km/h
      uvIndex: Math.round(day.uvi || 0),
      sunrise: this.formatUnixTime(day.sunrise),
      sunset: this.formatUnixTime(day.sunset),
      daylightHours: this.calculateDaylightHours(day.sunrise, day.sunset),
      dataSource: 'api'
    };
  }

  // Format Unix timestamp to HH:MM
  static formatUnixTime(unixTimestamp) {
    const date = new Date(unixTimestamp * 1000);
    return date.toLocaleTimeString(getDisplayLocale(), {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }

  // Calculate daylight hours from sunrise/sunset timestamps
  static calculateDaylightHours(sunriseUnix, sunsetUnix) {
    const hours = (sunsetUnix - sunriseUnix) / 3600;
    return Math.round(hours * 10) / 10;
  }

  // Fetch historical weather data from Open-Meteo (free, no API key needed)
  // Gets last year's weather for the same date to show typical conditions
  static async fetchHistoricalClimate(lat, lon, targetDate) {
    const date = new Date(targetDate);
    const month = date.getMonth();
    const cacheKey = `${lat.toFixed(1)},${lon.toFixed(1)},${month}`;

    // Check cache first
    const cached = this.CLIMATE_CACHE[cacheKey];
    if (cached && (Date.now() - cached.timestamp) < this.CLIMATE_CACHE_TTL_MS) {
      console.log('Using cached climate data for', cacheKey);
      return cached.data;
    }

    try {
      // Get data from last year for the same month (use full month for better averaging)
      const lastYear = date.getFullYear() - 1;
      const startDate = new Date(lastYear, month, 1);
      const endDate = new Date(lastYear, month + 1, 0); // Last day of month

      const startStr = startDate.toISOString().split('T')[0];
      const endStr = endDate.toISOString().split('T')[0];

      const url = `${this.OPEN_METEO_HISTORICAL_URL}?latitude=${lat}&longitude=${lon}&start_date=${startStr}&end_date=${endStr}&daily=temperature_2m_max,temperature_2m_min,precipitation_sum,weathercode&timezone=auto`;

      console.log('Fetching historical climate data from Open-Meteo:', url);
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Open-Meteo API failed: ${response.status}`);
      }

      const data = await response.json();

      if (data && data.daily) {
        // Calculate monthly averages
        const temps_max = data.daily.temperature_2m_max || [];
        const temps_min = data.daily.temperature_2m_min || [];
        const precip = data.daily.precipitation_sum || [];
        const weatherCodes = data.daily.weathercode || [];

        const avgHigh = temps_max.length > 0
          ? Math.round(temps_max.reduce((a, b) => a + b, 0) / temps_max.length)
          : null;
        const avgLow = temps_min.length > 0
          ? Math.round(temps_min.reduce((a, b) => a + b, 0) / temps_min.length)
          : null;
        const avgPrecip = precip.length > 0
          ? Math.round(precip.reduce((a, b) => a + b, 0) / precip.length * 10) / 10
          : 0;

        // Get most common weather code for typical conditions
        const typicalCode = this.getMostCommonWeatherCode(weatherCodes);

        const result = {
          high: avgHigh,
          low: avgLow,
          precipitation: avgPrecip,
          weatherCode: typicalCode,
          condition: this.mapOpenMeteoWeatherCode(typicalCode),
          month: month,
          dataSource: 'historical',
          yearOfData: lastYear
        };

        // Cache the result
        this.CLIMATE_CACHE[cacheKey] = {
          data: result,
          timestamp: Date.now()
        };

        console.log('Fetched historical climate for', cacheKey, ':', result);
        return result;
      }

      return null;
    } catch (error) {
      console.error('Open-Meteo historical API error:', error);
      return null;
    }
  }

  // Get most common weather code from array
  static getMostCommonWeatherCode(codes) {
    if (!codes || codes.length === 0) return 0;

    const counts = {};
    codes.forEach(code => {
      if (code !== null) {
        counts[code] = (counts[code] || 0) + 1;
      }
    });

    let maxCount = 0;
    let mostCommon = 0;
    Object.entries(counts).forEach(([code, count]) => {
      if (count > maxCount) {
        maxCount = count;
        mostCommon = parseInt(code);
      }
    });

    return mostCommon;
  }

  // Map Open-Meteo WMO weather codes to our condition strings
  // https://open-meteo.com/en/docs (WMO Weather interpretation codes)
  static mapOpenMeteoWeatherCode(code) {
    const codeMap = {
      0: 'clear',           // Clear sky
      1: 'clear',           // Mainly clear
      2: 'partly_cloudy',   // Partly cloudy
      3: 'cloudy',          // Overcast
      45: 'fog',            // Fog
      48: 'fog',            // Depositing rime fog
      51: 'light_rain',     // Light drizzle
      53: 'light_rain',     // Moderate drizzle
      55: 'rain',           // Dense drizzle
      61: 'light_rain',     // Slight rain
      63: 'rain',           // Moderate rain
      65: 'heavy_rain',     // Heavy rain
      71: 'snow',           // Slight snow
      73: 'snow',           // Moderate snow
      75: 'snow',           // Heavy snow
      80: 'light_rain',     // Slight rain showers
      81: 'rain',           // Moderate rain showers
      82: 'heavy_rain',     // Violent rain showers
      95: 'thunderstorm',   // Thunderstorm
      96: 'thunderstorm',   // Thunderstorm with hail
      99: 'thunderstorm'    // Thunderstorm with heavy hail
    };
    return codeMap[code] || 'partly_cloudy';
  }

  // Get current weather for a location
  static async getCurrentWeather(location) {
    const coords = await this.geocodeLocation(location);
    if (!coords) return this.getMockCurrentWeather(location);
    const weatherData = await this.fetchOneCallWeather(coords.latitude, coords.longitude);

    if (weatherData && weatherData.current) {
      return this.formatCurrentWeatherFromOneCall(weatherData.current, coords.name);
    }

    // Fallback to mock data
    console.log('Using mock weather data for', location);
    return this.getMockCurrentWeather(location);
  }

  // Format current weather from One Call API response
  static formatCurrentWeatherFromOneCall(current, locationName) {
    return {
      location: locationName,
      temperature: Math.round(current.temp),
      feelsLike: Math.round(current.feels_like),
      condition: current.weather?.[0]?.main || 'Clear',
      description: current.weather?.[0]?.description || 'clear',
      icon: current.weather?.[0]?.icon || '01d',
      humidity: current.humidity,
      windSpeed: Math.round(current.wind_speed * 3.6), // m/s to km/h
      precipitation: current.rain?.['1h'] || current.snow?.['1h'] || 0,
      visibility: current.visibility ? Math.round(current.visibility / 1000) : null,
      uvIndex: current.uvi,
      timestamp: new Date().toISOString()
    };
  }

  // Get weather forecast for next 5 days.
  // `knownCoords` ({latitude, longitude}) skips the geocoding round trip when
  // the caller already has coordinates (e.g. trip_days now persists them) —
  // faster, and immune to ambiguous names ("London, KY").
  static async getWeatherForecast(location, knownCoords = null) {
    const coords = this.isValidCoords(knownCoords)
      ? knownCoords
      : await this.geocodeLocation(location);
    // Unresolvable location — no forecast rather than someone else's.
    if (!coords) return [];
    const weatherData = await this.fetchOneCallWeather(coords.latitude, coords.longitude);

    if (weatherData && weatherData.daily) {
      return weatherData.daily.slice(0, 5).map(day => this.mapOneCallDayToInternal(day));
    }

    // Fallback to mock data
    console.log('Using mock forecast data for', location);
    return this.getMockForecast(location);
  }

  // The real forecast only, keyed by date ('YYYY-MM-DD') — for a surface
  // that matches many trip days at once (the itinerary's day headers).
  // Unlike getWeatherForecast: no mock fallback and no 5-day cut, so a day
  // has the real forecast or nothing. Each date is the PLACE's calendar day
  // (the response's timezone_offset), not the browser's: from the US, a
  // Tokyo day still matches Tokyo's forecast for it.
  static async getForecastByDate(location, knownCoords = null) {
    try {
      const coords = this.isValidCoords(knownCoords)
        ? knownCoords
        : await this.geocodeLocation(location);
      if (!coords) return {};
      const data = await this.fetchOneCallWeather(coords.latitude, coords.longitude);
      if (!Array.isArray(data?.daily)) return {};
      const offsetSeconds = Number(data.timezone_offset) || 0;
      const byDate = {};
      for (const day of data.daily) {
        const date = new Date((day.dt + offsetSeconds) * 1000).toISOString().split('T')[0];
        byDate[date] = { ...this.mapOneCallDayToInternal(day), date };
      }
      return byDate;
    } catch (error) {
      console.error('Forecast by date error:', error);
      return {};
    }
  }

  // True when the caller-supplied coords object is usable as-is.
  static isValidCoords(coords) {
    return !!coords && Number.isFinite(coords.latitude) && Number.isFinite(coords.longitude);
  }

  // Get weather for a specific date
  // Returns forecast data if within 8 days, otherwise returns historical climate averages.
  // `knownCoords` ({latitude, longitude}) skips geocoding — see getWeatherForecast.
  static async getWeatherForDate(location, date, knownCoords = null) {
    const targetDate = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysFromNow = Math.ceil((targetDate - today) / (1000 * 60 * 60 * 24));

    // If within forecast range (8 days), try to get real forecast
    if (daysFromNow >= 0 && daysFromNow < 8) {
      const forecast = await this.getWeatherForecast(location, knownCoords);
      const targetDateStr = targetDate.toDateString();
      const forecastDay = forecast.find(day => new Date(day.date).toDateString() === targetDateStr);
      if (forecastDay) {
        return forecastDay;
      }
    }

    // Beyond forecast range or no forecast found - get historical climate data
    const coords = this.isValidCoords(knownCoords)
      ? knownCoords
      : await this.geocodeLocation(location);
    // Unresolvable location — callers guard on null and show no weather.
    if (!coords) return null;
    const climateData = await this.fetchHistoricalClimate(coords.latitude, coords.longitude, targetDate);

    if (climateData) {
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];

      return {
        date: targetDate.toISOString().split('T')[0],
        high: climateData.high,
        low: climateData.low,
        condition: climateData.condition,
        conditions: climateData.condition,
        precipitation: climateData.precipitation,
        dataSource: 'historical',
        typicalFor: monthNames[targetDate.getMonth()],
        yearOfData: climateData.yearOfData
      };
    }

    // Final fallback to generated historical average
    return this.generateHistoricalAverageDay(location, targetDate);
  }

  // Format current weather data
  static formatCurrentWeather(data) {
    return {
      location: data.name,
      temperature: Math.round(data.main.temp),
      feelsLike: Math.round(data.main.feels_like),
      condition: data.weather[0].main,
      description: data.weather[0].description,
      icon: data.weather[0].icon,
      humidity: data.main.humidity,
      windSpeed: Math.round(data.wind?.speed || 0),
      precipitation: data.rain?.['1h'] || data.snow?.['1h'] || 0,
      visibility: data.visibility ? Math.round(data.visibility / 1000) : null,
      timestamp: new Date().toISOString()
    };
  }

  // Format forecast data
  static formatForecast(data) {
    const dailyForecasts = {};

    // Group by date and find daily highs/lows
    data.list.forEach(item => {
      const date = new Date(item.dt * 1000).toDateString();
      
      if (!dailyForecasts[date]) {
        dailyForecasts[date] = {
          date: new Date(item.dt * 1000).toISOString(),
          high: item.main.temp_max,
          low: item.main.temp_min,
          condition: item.weather[0].main,
          description: item.weather[0].description,
          icon: item.weather[0].icon,
          humidity: item.main.humidity,
          windSpeed: item.wind?.speed || 0,
          precipitation: item.rain?.['3h'] || item.snow?.['3h'] || 0,
          hourlyForecasts: []
        };
      } else {
        dailyForecasts[date].high = Math.max(dailyForecasts[date].high, item.main.temp_max);
        dailyForecasts[date].low = Math.min(dailyForecasts[date].low, item.main.temp_min);
      }

      // Add hourly forecast
      dailyForecasts[date].hourlyForecasts.push({
        time: new Date(item.dt * 1000).toISOString(),
        temperature: Math.round(item.main.temp),
        condition: item.weather[0].main,
        description: item.weather[0].description,
        icon: item.weather[0].icon,
        windSpeed: Math.round(item.wind?.speed || 0),
        precipitation: item.rain?.['3h'] || item.snow?.['3h'] || 0
      });
    });

    // Convert to array and round temperatures
    return Object.values(dailyForecasts).map(day => ({
      ...day,
      high: Math.round(day.high),
      low: Math.round(day.low),
      windSpeed: Math.round(day.windSpeed)
    })).slice(0, 5); // Limit to 5 days
  }

  // Mock current weather (fallback when API key not available)
  static getMockCurrentWeather(location) {
    const conditions = ['Clear', 'Partly Cloudy', 'Cloudy', 'Light Rain', 'Sunny'];
    const condition = conditions[Math.floor(Math.random() * conditions.length)];
    
    return {
      location: location,
      temperature: Math.floor(Math.random() * 30) + 55, // 55-85°F
      feelsLike: Math.floor(Math.random() * 30) + 55,
      condition: condition,
      description: condition.toLowerCase(),
      icon: condition === 'Clear' ? '01d' : condition === 'Sunny' ? '01d' : '02d',
      humidity: Math.floor(Math.random() * 40) + 40, // 40-80%
      windSpeed: Math.floor(Math.random() * 15) + 5, // 5-20 mph
      precipitation: condition.includes('Rain') ? Math.random() * 0.5 : 0,
      visibility: 10,
      timestamp: new Date().toISOString()
    };
  }

  // Mock forecast (fallback when API key not available)
  static getMockForecast(location) {
    const forecast = [];
    const today = new Date();
    
    for (let i = 0; i < 5; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      const conditions = ['Clear', 'Partly Cloudy', 'Cloudy', 'Light Rain', 'Sunny'];
      const condition = conditions[Math.floor(Math.random() * conditions.length)];
      
      forecast.push({
        date: date.toISOString(),
        // Celsius, to match every other path — the whole service requests
        // `units=metric`. These were Fahrenheit, which made `high >= 30`
        // (the "hot" test in consumers) true for every mock day.
        high: Math.floor(Math.random() * 14) + 18, // 18-31°C
        low: Math.floor(Math.random() * 10) + 8,   // 8-17°C
        condition: condition,
        conditions: condition,
        temperature: {
          high: Math.floor(Math.random() * 14) + 18,
          low: Math.floor(Math.random() * 10) + 8
        },
        description: condition.toLowerCase(),
        icon: condition === 'Clear' ? '01d' : condition === 'Sunny' ? '01d' : '02d',
        humidity: Math.floor(Math.random() * 40) + 40,
        windSpeed: Math.floor(Math.random() * 15) + 5,
        precipitation: {
          probability: condition.includes('Rain') ? Math.floor(Math.random() * 60) + 40 : Math.floor(Math.random() * 20),
          expectedMm: condition.includes('Rain') ? Math.floor(Math.random() * 8) + 1 : 0
        },
        hourlyForecasts: []
      });
    }
    
    return forecast;
  }

  // Check if weather is good for outdoor activities
  static isGoodWeatherForOutdoor(weatherData) {
    if (!weatherData) return true; // Default to allowing activity if no weather data

    const { temperature, condition, windSpeed, precipitation } = weatherData;
    
    // Check for bad conditions
    if (condition?.toLowerCase().includes('rain') || 
        condition?.toLowerCase().includes('storm') || 
        condition?.toLowerCase().includes('snow')) {
      return false;
    }

    if (precipitation > 0.1) {
      return false;
    }

    if (windSpeed > 25) {
      return false;
    }

    if (temperature < 35 || temperature > 95) {
      return false;
    }

    return true;
  }

  // Get weather Icon component name for display (see getWeatherIcon for the emoji variant)
  static getWeatherIconName(condition) {
    const iconMap = {
      'clear': 'sun',
      'sunny': 'sun',
      'partly cloudy': 'sunCloud',
      'cloudy': 'cloud',
      'overcast': 'cloud',
      'rain': 'cloudRain',
      'light rain': 'sunCloud',
      'heavy rain': 'cloudRain',
      'thunderstorm': 'bolt',
      'snow': 'snowflake',
      'fog': 'fog',
      'mist': 'fog'
    };

    const key = condition?.toLowerCase() || 'clear';
    return iconMap[key] || 'sunCloud';
  }

  // Get coordinates for a location (using fallback data only - no API calls)
  static async getLocationCoordinates(location) {
    // Always use fallback coordinates instead of API calls
    return this.getFallbackCoordinates(location);
  }

  // Get fallback coordinates from our hardcoded database
  static getFallbackCoordinates(location) {
    const fallbacks = {
      // Europe
      'sweden': { latitude: 59.3293, longitude: 18.0686, name: 'Stockholm', country: 'Sweden' },
      'stockholm': { latitude: 59.3293, longitude: 18.0686, name: 'Stockholm', country: 'Sweden' },
      'norway': { latitude: 59.9139, longitude: 10.7522, name: 'Oslo', country: 'Norway' },
      'oslo': { latitude: 59.9139, longitude: 10.7522, name: 'Oslo', country: 'Norway' },
      'brussels': { latitude: 50.8503, longitude: 4.3517, name: 'Brussels', country: 'Belgium' },
      'belgium': { latitude: 50.8503, longitude: 4.3517, name: 'Brussels', country: 'Belgium' },
      'paris': { latitude: 48.8566, longitude: 2.3522, name: 'Paris', country: 'France' },
      'france': { latitude: 48.8566, longitude: 2.3522, name: 'Paris', country: 'France' },
      'london': { latitude: 51.5074, longitude: -0.1278, name: 'London', country: 'United Kingdom' },
      'amsterdam': { latitude: 52.3676, longitude: 4.9041, name: 'Amsterdam', country: 'Netherlands' },
      'berlin': { latitude: 52.5200, longitude: 13.4050, name: 'Berlin', country: 'Germany' },
      'rome': { latitude: 41.9028, longitude: 12.4964, name: 'Rome', country: 'Italy' },
      'barcelona': { latitude: 41.3851, longitude: 2.1734, name: 'Barcelona', country: 'Spain' },
      'madrid': { latitude: 40.4168, longitude: -3.7038, name: 'Madrid', country: 'Spain' },

      // North America
      'san francisco': { latitude: 37.7749, longitude: -122.4194, name: 'San Francisco', country: 'United States' },
      'new york': { latitude: 40.7128, longitude: -74.0060, name: 'New York', country: 'United States' },
      'los angeles': { latitude: 34.0522, longitude: -118.2437, name: 'Los Angeles', country: 'United States' },
      'chicago': { latitude: 41.8781, longitude: -87.6298, name: 'Chicago', country: 'United States' },
      'miami': { latitude: 25.7617, longitude: -80.1918, name: 'Miami', country: 'United States' },
      'denver': { latitude: 39.7392, longitude: -104.9903, name: 'Denver', country: 'United States' },
      'seattle': { latitude: 47.6062, longitude: -122.3321, name: 'Seattle', country: 'United States' },
      'toronto': { latitude: 43.6532, longitude: -79.3832, name: 'Toronto', country: 'Canada' },
      'vancouver': { latitude: 49.2827, longitude: -123.1207, name: 'Vancouver', country: 'Canada' },
      'cancun': { latitude: 21.1619, longitude: -86.8515, name: 'Cancun', country: 'Mexico' },

      // Asia
      'tokyo': { latitude: 35.6762, longitude: 139.6503, name: 'Tokyo', country: 'Japan' },
      'japan': { latitude: 35.6762, longitude: 139.6503, name: 'Tokyo', country: 'Japan' },
      'thailand': { latitude: 13.7563, longitude: 100.5018, name: 'Bangkok', country: 'Thailand' },
      'bangkok': { latitude: 13.7563, longitude: 100.5018, name: 'Bangkok', country: 'Thailand' },
      'singapore': { latitude: 1.3521, longitude: 103.8198, name: 'Singapore', country: 'Singapore' },
      'hong kong': { latitude: 22.3193, longitude: 114.1694, name: 'Hong Kong', country: 'Hong Kong' },
      'seoul': { latitude: 37.5665, longitude: 126.9780, name: 'Seoul', country: 'South Korea' },
      'bali': { latitude: -8.3405, longitude: 115.0920, name: 'Bali', country: 'Indonesia' },

      // Middle East
      'dubai': { latitude: 25.2048, longitude: 55.2708, name: 'Dubai', country: 'United Arab Emirates' },
      'abu dhabi': { latitude: 24.4539, longitude: 54.3773, name: 'Abu Dhabi', country: 'United Arab Emirates' },

      // Oceania
      'sydney': { latitude: -33.8688, longitude: 151.2093, name: 'Sydney', country: 'Australia' },
      'melbourne': { latitude: -37.8136, longitude: 144.9631, name: 'Melbourne', country: 'Australia' },
      'auckland': { latitude: -36.8509, longitude: 174.7645, name: 'Auckland', country: 'New Zealand' },

      // South America
      'rio de janeiro': { latitude: -22.9068, longitude: -43.1729, name: 'Rio de Janeiro', country: 'Brazil' },
      'buenos aires': { latitude: -34.6037, longitude: -58.3816, name: 'Buenos Aires', country: 'Argentina' }
    };

    // Try to find a match with cleaned location
    const cleanedLower = this.cleanLocationString(location).toLowerCase();
    const directMatch = fallbacks[cleanedLower];
    if (directMatch) return directMatch;

    // Try matching just the city name (first part before comma)
    const cityOnly = cleanedLower.split(',')[0].trim();
    if (fallbacks[cityOnly]) return fallbacks[cityOnly];

    // No match. Deliberately return null rather than defaulting to a real
    // city: callers render the place name they were given, so any default
    // here produces a confident claim about the wrong place ("Cold in Ubud"
    // from New York's climate). Every caller treats null as "no weather",
    // which is the honest answer.
    return null;
  }

  // Get daylight hours (using calculated data only - no API calls)
  static async getDaylightHours(location, date) {
    // Always use calculated data instead of API calls
    return this.getCalculatedDaylightHours(location, date);
  }

  // Fallback calculation method (original implementation)
  static getCalculatedDaylightHours(location, date) {
    const coords = this.getFallbackCoordinates(location);
    const targetDate = new Date(date);
    
    // Calculate day of year
    const start = new Date(targetDate.getFullYear(), 0, 0);
    const diff = targetDate - start;
    const dayOfYear = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    // Calculate solar declination
    const declination = 23.45 * Math.sin((360 * (284 + dayOfYear) / 365) * Math.PI / 180);
    
    // Calculate hour angle
    const latitude = coords.latitude;
    const hourAngle = Math.acos(-Math.tan(latitude * Math.PI / 180) * Math.tan(declination * Math.PI / 180));
    
    // Calculate daylight duration
    const daylightHours = (2 * hourAngle * 180 / Math.PI) / 15;
    
    // Calculate sunrise and sunset times
    const noon = 12;
    const sunrise = noon - (daylightHours / 2);
    const sunset = noon + (daylightHours / 2);
    
    return {
      sunrise: this.formatTime(sunrise),
      sunset: this.formatTime(sunset),
      daylightHours: Math.round(daylightHours * 10) / 10,
      isExtremeDaylight: daylightHours > 18 || daylightHours < 6,
      coordinates: coords
    };
  }

  // Format time from Date object to HH:MM (local time)
  static formatTimeFromDate(date) {
    return date.toLocaleTimeString(getDisplayLocale(), { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit',
      timeZone: 'UTC' // Use UTC to avoid timezone conversion issues
    });
  }

  // Format time from decimal hours to HH:MM
  static formatTime(decimalHours) {
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  // Get seasonal daylight recommendations
  static getDaylightRecommendations(location, date) {
    const daylight = this.getDaylightHours(location, date);
    const recommendations = [];

    if (daylight.daylightHours > 18) {
      recommendations.push({
        type: 'daylight',
        icon: '🌅',
        message: `Extended daylight (${daylight.daylightHours}h) - perfect for late evening activities until ${daylight.sunset}`
      });
      recommendations.push({
        type: 'schedule',
        icon: '🏃',
        message: 'Consider scheduling outdoor activities later in the day when it\'s cooler'
      });
    } else if (daylight.daylightHours < 8) {
      recommendations.push({
        type: 'daylight',
        icon: '🌆',
        message: `Limited daylight (${daylight.daylightHours}h) - plan indoor activities after ${daylight.sunset}`
      });
      recommendations.push({
        type: 'schedule',
        icon: '⏰',
        message: 'Schedule outdoor activities between 10 AM and 3 PM for maximum daylight'
      });
    } else if (daylight.sunset <= '16:00') {
      recommendations.push({
        type: 'early_sunset',
        icon: '🌇',
        message: `Early sunset at ${daylight.sunset} - plan dinner and indoor activities for evening`
      });
    } else if (daylight.sunset >= '21:00') {
      recommendations.push({
        type: 'late_sunset',
        icon: '🌄',
        message: `Late sunset at ${daylight.sunset} - extended time for outdoor activities and sightseeing`
      });
    }

    return recommendations;
  }

  // Enhanced location database with latitude data
  static HISTORICAL_DATA = {
    'sweden': {
      latitude: 62,
      months: {
        0: { temp: { high: -1, low: -7 }, precipitation: 39, season: 'winter' },
        1: { temp: { high: 0, low: -6 }, precipitation: 27, season: 'winter' },
        2: { temp: { high: 3, low: -4 }, precipitation: 26, season: 'winter' },
        3: { temp: { high: 9, low: 0 }, precipitation: 31, season: 'spring' },
        4: { temp: { high: 16, low: 6 }, precipitation: 30, season: 'spring' },
        5: { temp: { high: 21, low: 11 }, precipitation: 45, season: 'summer' },
        6: { temp: { high: 24, low: 14 }, precipitation: 62, season: 'summer' },
        7: { temp: { high: 22, low: 13 }, precipitation: 66, season: 'summer' },
        8: { temp: { high: 17, low: 9 }, precipitation: 55, season: 'autumn' },
        9: { temp: { high: 11, low: 4 }, precipitation: 44, season: 'autumn' },
        10: { temp: { high: 5, low: 0 }, precipitation: 50, season: 'autumn' },
        11: { temp: { high: 1, low: -4 }, precipitation: 46, season: 'winter' }
      }
    },
    'norway': {
      latitude: 65,
      months: {
        0: { temp: { high: -2, low: -8 }, precipitation: 68, season: 'winter' },
        1: { temp: { high: -1, low: -7 }, precipitation: 52, season: 'winter' },
        2: { temp: { high: 2, low: -5 }, precipitation: 50, season: 'winter' },
        3: { temp: { high: 6, low: -1 }, precipitation: 45, season: 'spring' },
        4: { temp: { high: 12, low: 4 }, precipitation: 40, season: 'spring' },
        5: { temp: { high: 17, low: 9 }, precipitation: 50, season: 'summer' },
        6: { temp: { high: 20, low: 12 }, precipitation: 65, season: 'summer' },
        7: { temp: { high: 18, low: 11 }, precipitation: 81, season: 'summer' },
        8: { temp: { high: 14, low: 7 }, precipitation: 89, season: 'autumn' },
        9: { temp: { high: 9, low: 3 }, precipitation: 84, season: 'autumn' },
        10: { temp: { high: 3, low: -2 }, precipitation: 84, season: 'autumn' },
        11: { temp: { high: 0, low: -6 }, precipitation: 79, season: 'winter' }
      }
    },
    'brussels': {
      latitude: 50.8,
      months: {
        0: { temp: { high: 6, low: 1 }, precipitation: 67, season: 'winter' },
        1: { temp: { high: 7, low: 1 }, precipitation: 53, season: 'winter' },
        2: { temp: { high: 11, low: 3 }, precipitation: 50, season: 'spring' },
        3: { temp: { high: 14, low: 5 }, precipitation: 57, season: 'spring' },
        4: { temp: { high: 18, low: 8 }, precipitation: 57, season: 'spring' },
        5: { temp: { high: 22, low: 12 }, precipitation: 75, season: 'summer' },
        6: { temp: { high: 24, low: 14 }, precipitation: 78, season: 'summer' },
        7: { temp: { high: 23, low: 14 }, precipitation: 85, season: 'summer' },
        8: { temp: { high: 19, low: 11 }, precipitation: 82, season: 'autumn' },
        9: { temp: { high: 14, low: 7 }, precipitation: 78, season: 'autumn' },
        10: { temp: { high: 9, low: 3 }, precipitation: 84, season: 'autumn' },
        11: { temp: { high: 6, low: 1 }, precipitation: 76, season: 'winter' }
      }
    },
    'belgium': {
      latitude: 50.8,
      months: {
        0: { temp: { high: 6, low: 1 }, precipitation: 67, season: 'winter' },
        1: { temp: { high: 7, low: 1 }, precipitation: 53, season: 'winter' },
        2: { temp: { high: 11, low: 3 }, precipitation: 50, season: 'spring' },
        3: { temp: { high: 14, low: 5 }, precipitation: 57, season: 'spring' },
        4: { temp: { high: 18, low: 8 }, precipitation: 57, season: 'spring' },
        5: { temp: { high: 22, low: 12 }, precipitation: 75, season: 'summer' },
        6: { temp: { high: 24, low: 14 }, precipitation: 78, season: 'summer' },
        7: { temp: { high: 23, low: 14 }, precipitation: 85, season: 'summer' },
        8: { temp: { high: 19, low: 11 }, precipitation: 82, season: 'autumn' },
        9: { temp: { high: 14, low: 7 }, precipitation: 78, season: 'autumn' },
        10: { temp: { high: 9, low: 3 }, precipitation: 84, season: 'autumn' },
        11: { temp: { high: 6, low: 1 }, precipitation: 76, season: 'winter' }
      }
    }
  };

  // Get weather for trip dates (main method used by WeatherForecast component)
  static async getWeatherForTrip(location, startDate, endDate) {
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      // Compare calendar days only. `start` is parsed as UTC midnight from a YYYY-MM-DD
      // string, so use its UTC calendar date; `now` is local, so use its local calendar
      // date. Both get normalized via Date.UTC to avoid DST/timezone off-by-one.
      const now = new Date();
      const startUTC = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
      const todayUTC = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
      const daysUntilTrip = Math.round((startUTC - todayUTC) / 86400000);

      // Geocode the location first
      const coords = await this.geocodeLocation(location);
      if (!coords) {
        return { success: false, error: 'Unable to load weather information' };
      }
      const resolvedLocation = coords.name || location;

      // Try to fetch real API data
      // Fetch real forecast data via backend proxy (returns null if weather API not configured)
      let apiData = await this.fetchOneCallWeather(coords.latitude, coords.longitude);

      // Build a map of API forecast data by date string for reliable matching
      const apiDaysByDate = {};
      if (apiData && apiData.daily) {
        apiData.daily.forEach(day => {
          const dateStr = new Date(day.dt * 1000).toISOString().split('T')[0];
          apiDaysByDate[dateStr] = day;
        });
      }

      const weather = [];
      const currentDate = new Date(start);

      // Build weather data for each day of the trip
      while (currentDate <= end) {
        // Use date string for matching instead of offset calculation
        const currentDateStr = currentDate.toISOString().split('T')[0];
        let dayData;

        // Use API data if available for this date
        const apiDay = apiDaysByDate[currentDateStr];
        if (apiDay) {
          dayData = this.mapOneCallDayToInternal(apiDay);
        }

        // Fall back to historical data if API data not available for this day
        if (!dayData) {
          // Try to get real historical climate data from Open-Meteo first
          const climateData = await this.fetchHistoricalClimate(coords.latitude, coords.longitude, currentDate);

          if (climateData) {
            dayData = {
              date: currentDateStr,
              conditions: climateData.condition,
              temperature: {
                high: climateData.high,
                low: climateData.low
              },
              precipitation: {
                probability: Math.min(Math.floor(climateData.precipitation * 10), 90),
                expectedMm: Math.round(climateData.precipitation)
              },
              humidity: 65,
              windSpeed: 15,
              uvIndex: 5,
              dataSource: 'historical_average',
              yearsOfData: 1
            };
          } else {
            // Final fallback to generated historical average
            dayData = this.generateHistoricalAverageDay(location, new Date(currentDate));
          }

          // Add daylight info for non-API days
          const daylightInfo = await this.getDaylightHours(location, currentDate);
          dayData.daylightHours = daylightInfo.daylightHours;
          dayData.sunrise = daylightInfo.sunrise;
          dayData.sunset = daylightInfo.sunset;
        }

        weather.push(dayData);
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // Determine data type based on what we used
      const hasApiData = weather.some(day => day.dataSource === 'api');
      const dataType = hasApiData ? 'forecast' : 'historical_average';

      // Get seasonal information and recommendations
      const seasonalInfo = this.getSeasonalInfo(location, start);
      const daylightRecommendations = this.getDaylightRecommendations(location, startDate);
      const recommendations = [...daylightRecommendations];

      return {
        success: true,
        weather,
        seasonalInfo,
        recommendations,
        dataType,
        daysUntilTrip,
        location: resolvedLocation
      };
    } catch (error) {
      console.error('Error getting weather for trip:', error);
      return {
        success: false,
        error: 'Unable to load weather information'
      };
    }
  }

  // Generate mock forecast day data
  static generateMockForecastDay(location, date) {
    const conditions = ['clear', 'partly_cloudy', 'cloudy', 'light_rain', 'sunny'];
    const condition = conditions[Math.floor(Math.random() * conditions.length)];
    
    const high = Math.floor(Math.random() * 15) + 20; // 20-35°C
    const low = Math.floor(Math.random() * 10) + 10;  // 10-20°C

    return {
      date: date.toISOString().split('T')[0],
      conditions: condition,
      // Flat fields too — see mapOneCallDayToInternal, which is the shape
      // consumers actually read.
      condition,
      high,
      low,
      temperature: { high, low },
      precipitation: {
        probability: condition.includes('rain') ? Math.floor(Math.random() * 60) + 40 : Math.floor(Math.random() * 20),
        expectedMm: condition.includes('rain') ? Math.floor(Math.random() * 10) + 2 : 0
      },
      humidity: Math.floor(Math.random() * 30) + 50, // 50-80%
      windSpeed: Math.floor(Math.random() * 15) + 5, // 5-20 km/h
      uvIndex: Math.floor(Math.random() * 8) + 3,    // 3-10
      dataSource: 'forecast'
    };
  }

  // Generate historical average day data
  static generateHistoricalAverageDay(location, date) {
    const locationData = this.HISTORICAL_DATA[location.toLowerCase()];
    const month = date.getMonth();
    
    if (locationData && locationData.months[month]) {
      const monthData = locationData.months[month];
      const condition = monthData.season === 'winter' ? 'cloudy' : 'partly_cloudy';
      return {
        date: date.toISOString().split('T')[0],
        conditions: condition,
        // Flat fields too — see mapOneCallDayToInternal.
        condition,
        high: monthData.temp.high,
        low: monthData.temp.low,
        temperature: {
          high: monthData.temp.high,
          low: monthData.temp.low
        },
        precipitation: {
          probability: Math.min(Math.floor(monthData.precipitation * 2), 90),
          expectedMm: Math.floor(monthData.precipitation / 10)
        },
        humidity: Math.floor(Math.random() * 20) + 60,
        windSpeed: Math.floor(Math.random() * 10) + 10,
        uvIndex: monthData.season === 'summer' ? Math.floor(Math.random() * 3) + 7 : Math.floor(Math.random() * 4) + 2,
        dataSource: 'historical_average',
        yearsOfData: Math.floor(Math.random() * 20) + 10 // 10-30 years
      };
    }
    
    // Fallback for unknown locations
    return this.generateMockForecastDay(location, date);
  }

  // Get seasonal information for a location and date
  static getSeasonalInfo(location, date) {
    const locationData = this.HISTORICAL_DATA[location.toLowerCase()];
    const month = date.getMonth();
    
    if (!locationData || !locationData.months[month]) {
      return null;
    }

    const monthData = locationData.months[month];
    const seasonInfo = {
      name: `${monthData.season.charAt(0).toUpperCase() + monthData.season.slice(1)} in ${location}`,
      description: this.getSeasonDescription(location, monthData.season, month),
      highlights: this.getSeasonHighlights(location, monthData.season),
      challenges: this.getSeasonChallenges(location, monthData.season, monthData),
      packingTips: this.getPackingTips(monthData.season, monthData.temp),
      bestActivities: this.getBestActivities(monthData.season, location),
      worstActivities: this.getWorstActivities(monthData.season, location)
    };

    return seasonInfo;
  }

  // Helper methods for seasonal information
  static getSeasonDescription(location, season, month) {
    const descriptions = {
      'winter': `Winter months bring cold temperatures and shorter days. ${location} experiences its coldest period.`,
      'spring': `Spring brings milder weather and longer days. A pleasant time to visit ${location}.`,
      'summer': `Summer offers the warmest weather and longest days. Peak travel season for ${location}.`,
      'autumn': `Autumn features cooler temperatures and changing landscapes. Beautiful season in ${location}.`
    };
    return descriptions[season] || 'Seasonal weather information';
  }

  static getSeasonHighlights(location, season) {
    const highlights = {
      'winter': ['Fewer crowds', 'Lower prices', 'Cozy indoor activities'],
      'spring': ['Mild temperatures', 'Blooming flowers', 'Comfortable walking weather'],
      'summer': ['Long daylight hours', 'Warmest weather', 'Outdoor festivals'],
      'autumn': ['Beautiful fall colors', 'Comfortable temperatures', 'Harvest season']
    };
    return highlights[season] || [];
  }

  static getSeasonChallenges(location, season, monthData) {
    const challenges = {
      'winter': ['Short daylight hours', 'Cold temperatures', 'Potential snow'],
      'spring': ['Variable weather', 'Occasional rain'],
      'summer': ['Hot temperatures', 'Tourist crowds', 'Higher prices'],
      'autumn': ['Shorter days', 'Unpredictable weather']
    };
    
    const seasonChallenges = challenges[season] || [];
    
    // Add specific challenges based on temperature and precipitation
    if (monthData.temp.low < 0) {
      seasonChallenges.push('Freezing temperatures');
    }
    if (monthData.precipitation > 80) {
      seasonChallenges.push('Heavy rainfall');
    }
    
    return seasonChallenges;
  }

  static getPackingTips(season, temperature) {
    const tips = {
      'winter': ['Warm coat', 'Insulated boots', 'Gloves and hat', 'Layers'],
      'spring': ['Light jacket', 'Comfortable shoes', 'Rain jacket', 'Layers'],
      'summer': ['Light clothing', 'Sunscreen', 'Hat', 'Comfortable shoes'],
      'autumn': ['Medium jacket', 'Warm layers', 'Rain gear', 'Closed shoes']
    };
    return tips[season] || [];
  }

  static getBestActivities(season, location) {
    const activities = {
      'winter': ['Museums', 'Indoor dining', 'Shopping', 'Hot springs'],
      'spring': ['Walking tours', 'Parks', 'Outdoor cafes', 'Gardens'],
      'summer': ['Hiking', 'Outdoor festivals', 'Beach activities', 'Sightseeing'],
      'autumn': ['Photography', 'Parks', 'Markets', 'Cultural sites']
    };
    return activities[season] || [];
  }

  static getWorstActivities(season, location) {
    const activities = {
      'winter': ['Outdoor swimming', 'Beach activities', 'Long walks'],
      'spring': ['Heavy outdoor activities in rain'],
      'summer': ['Intense hiking in heat', 'Heavy clothing activities'],
      'autumn': ['Light clothing activities', 'Late evening outdoor activities']
    };
    return activities[season] || [];
  }

  // Temperature formatting utility
  static formatTemperature(celsius, unit = 'C') {
    if (unit === 'F') {
      return Math.round((celsius * 9/5) + 32);
    }
    return Math.round(celsius);
  }

  // Get weather icon for conditions
  static getWeatherIcon(condition) {
    const iconMap = {
      'clear': '☀️',
      'sunny': '☀️',
      'partly_cloudy': '⛅',
      'cloudy': '☁️',
      'overcast': '☁️',
      'light_rain': '🌦️',
      'rain': '🌧️',
      'heavy_rain': '🌧️',
      'thunderstorm': '⛈️',
      'snow': '❄️',
      'fog': '🌫️',
      'mist': '🌫️'
    };
    return iconMap[condition] || '🌤️';
  }

  // Get weather advice for activities
  static getWeatherAdvice(weatherData, activityType = 'outdoor') {
    if (!weatherData) return null;

    const { temperature, condition, windSpeed, precipitation } = weatherData;
    const advice = [];

    if (activityType === 'outdoor') {
      if (temperature < 45) {
        advice.push('🧥 Dress warmly - it\'s quite cold');
      } else if (temperature > 85) {
        advice.push('🌞 Stay hydrated and wear sunscreen');
      }

      if (windSpeed > 15) {
        advice.push('💨 It\'s windy - secure loose items');
      }

      if (precipitation > 0) {
        advice.push('☔ Bring an umbrella or rain gear');
      }

      if (condition?.toLowerCase().includes('sun')) {
        advice.push('🕶️ Consider sunglasses');
      }
    }

    return advice.length > 0 ? advice : ['🌤️ Perfect weather for your activity!'];
  }
}

export default WeatherService;